import { useMemo } from 'react';
import {
  useCurrentFrame,
  interpolate,
  useVideoConfig,
  spring,
  Easing,
} from 'remotion';

// Components
import { FlipNumber } from './FlipNumber';

// Theme
import { employeeYearInReviewTheme } from '@/components/EmployeeYearInReview/theme';

// Utils
import { combineClasses } from '@/utils/className';
import {
  calculateFlipSchedule,
  getFlipState,
  getTextFillStyle,
  getBackgroundFillStyle,
} from './utils';

// Constants
import { FLIP_COUNTER_CONFIG, TEXT_FILL_ANIMATION } from './constants';
import { CLAMP_RIGHT, ANNIVERSARY_ANIM } from '../constants';
import { CLAMP_BOTH } from '@/constants/animations';

interface EngagedFlipCounterProps {
  value: number;
  label?: string;
  unit: string;
  startFrame?: number;
  totalFrames?: number;
  fontSize?: number;
  className?: string;
  showFillAnimation?: boolean;
  fillAnimationStartFrame?: number;
}

/**
 * Animated flip counter that counts up from 0 to target value
 * with a settle bounce effect when finished
 */
export const EngagedFlipCounter = ({
  value,
  label,
  unit,
  startFrame = 0,
  totalFrames = FLIP_COUNTER_CONFIG.DEFAULT_TOTAL_FRAMES,
  fontSize,
  className,
  showFillAnimation = false,
  fillAnimationStartFrame = 0,
}: EngagedFlipCounterProps) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  // Calculate when each number should flip during the animation
  const schedule = useMemo(
    () => calculateFlipSchedule(value, totalFrames, startFrame),
    [value, totalFrames, startFrame],
  );

  // Get current flip state based on frame and schedule
  const { currentValue, nextValue, flipProgress } = getFlipState(
    frame,
    startFrame,
    totalFrames,
    value,
    schedule,
  );

  // Settle animation: slight bounce effect when counter finishes
  const finished = frame >= startFrame + totalFrames;
  const settleProgress = spring({
    frame: frame - (startFrame + totalFrames),
    fps,
    config: FLIP_COUNTER_CONFIG.SETTLE_SPRING,
  });

  const scale = finished
    ? interpolate(
        settleProgress,
        [0, FLIP_COUNTER_CONFIG.SETTLE_SCALE.duration, 1],
        [1, FLIP_COUNTER_CONFIG.SETTLE_SCALE.max, 1],
        CLAMP_RIGHT,
      )
    : 1;

  const { colors, rawColors } = employeeYearInReviewTheme;
  const fillOpts = [
    frame,
    fillAnimationStartFrame,
    TEXT_FILL_ANIMATION.FILL_DURATION,
  ] as const;

  const fall = ANNIVERSARY_ANIM.FALL_DURATION;
  const riseDelay = ANNIVERSARY_ANIM.RISE_DELAY;
  const rise = ANNIVERSARY_ANIM.RISE_DURATION;

  const fallRotate = interpolate(
    frame,
    [fillAnimationStartFrame, fillAnimationStartFrame + fall],
    [0, 90],
    {
      ...CLAMP_BOTH,
      easing: Easing.in(Easing.ease),
    },
  );

  const riseRotate = interpolate(
    frame,
    [
      fillAnimationStartFrame + riseDelay,
      fillAnimationStartFrame + riseDelay + rise,
    ],
    [90, 0],
    {
      ...CLAMP_BOTH,
      easing: Easing.out(Easing.ease),
    },
  );

  const isRising = frame >= fillAnimationStartFrame + riseDelay;
  const rotateX = isRising ? riseRotate : fallRotate;

  const bgFillStyle =
    showFillAnimation && isRising
      ? getBackgroundFillStyle(
          ...fillOpts,
          rawColors.goldSecondary,
          rawColors.goldSecondary,
        )
      : undefined;

  return (
    <div
      className={combineClasses('size-full perspective-1000', className)}
      style={{
        transform: `scale(${scale}) rotateX(${showFillAnimation ? rotateX : 0}deg)`,
      }}
    >
      <FlipNumber
        value={currentValue}
        nextValue={nextValue}
        progress={flipProgress}
        fontSize={fontSize}
        unit={unit}
        className={combineClasses(colors.bgSlateDeep, colors.textPrimary)}
        bgStyle={bgFillStyle}
        {...(showFillAnimation && {
          textStyle: getTextFillStyle(
            ...fillOpts,
            employeeYearInReviewTheme.rawColors.textSecondary,
          ),
        })}
      />

      {label && (
        <span
          className={combineClasses(
            'absolute -top-10 left-0 right-0',
            `text-center ${colors.textPrimary} text-lg`,
            'opacity-80 tracking-widest',
          )}
        >
          {label}
        </span>
      )}
    </div>
  );
};
